/**
 * 流水页的**中文名唯一源** + 行文案派生（纯逻辑 · 零 DOM）
 *
 * ## 🔴 为什么单独一个文件
 * 与 `account-labels.ts` / `policy-status.ts` 同一理由：同一个中文名若在
 * 筛选轨、行、抽屉三处各写一遍，改名时必漏一处 —— 而**漏改不会报错**（只是两处不一致）。
 * ⇒ 全部收敛到本文件；枚举取值集用**闭合 `Record`**（新增枚举值 = 编译错误，不是静默缺一档）。
 *
 * ## 🔴 本文件里的判据一律"转发"，不自己判
 * 形态（转账 / 多腿）、是否标注"不计收支"、来源 —— 都取**服务端派生好的字段**
 * （`parent.kind` / `parent.hasExcludedLegs` / `row.source`）。
 * 端侧**不做形态判定**（`AC-C1.7` 的判定在共享层 `deriveParentRow`，此处只选文案）。
 */

import {
  ParentRowBusinessKind,
  ParentRowKind,
  TxnDirectionFilter,
  TxnSourceFilter,
  TxnStatusFilter,
  formatMoneyText,
  netAfterRefund,
  sumCents,
  toCents,
  type Cents,
  type TransactionLegView,
  type TransactionRowView,
} from '@jiaying/shared';
import { JournalWindow } from './journal-filter.js';

// ═══════════════════════ 各维度的选项（顺序 = 展示顺序） ═══════════════════════

export interface FilterOption<T extends string> {
  id: T;
  label: string;
  /** 补充说明（如"默认"）—— 只展示，不参与任何判定 */
  hint?: string;
}

/** 「默认」标注文案（只有一档会带它 —— 见 `windowOptionsFor`） */
export const DEFAULT_WINDOW_HINT = '默认';

/**
 * 全量时间档（**不带**「默认」标注）—— 顺序 = 展示顺序：
 * C 域 PRD §5.1.4 的三项（近 3 月 / 本月 / 上月 / 今年至今 / 自定义）+ 🆕 N 域批 `59` 的两个滚动档
 * （近 1 月 / 近 6 月，`AC-N2.18` 的 1 / 3 / 6 月需要一一对应的落点）。
 */
const WINDOW_LIST: readonly FilterOption<JournalWindow>[] = [
  { id: JournalWindow.Recent3m, label: '近 3 个月' },
  { id: JournalWindow.Recent1m, label: '近 1 个月' },
  { id: JournalWindow.Recent6m, label: '近 6 个月' },
  { id: JournalWindow.ThisMonth, label: '本月' },
  { id: JournalWindow.LastMonth, label: '上月' },
  { id: JournalWindow.ThisYear, label: '今年至今' },
  { id: JournalWindow.Custom, label: '自定义区间' },
];

/**
 * 时间档选项，**「默认」标注落在 `base` 那一档**。
 *
 * 🔴 为什么不是写死的 `hint: '默认'` 常量：用户把默认窗口设成「近 6 月」后，
 * 把「默认」标在「近 3 月」上是**假信息**（他会以为自己进错档了）。
 * 标注必须跟着**这个用户的基线**走 —— 与 `activeDimensionCount(draft, base)` 同源。
 */
export function windowOptionsFor(base: JournalWindow): readonly FilterOption<JournalWindow>[] {
  return WINDOW_LIST.map((o) => (o.id === base ? { ...o, hint: DEFAULT_WINDOW_HINT } : o));
}

/** 默认基线下（近 3 月）的选项表 —— 供"无偏好上下文"处（如单测）直接使用 */
export const WINDOW_OPTIONS: readonly FilterOption<JournalWindow>[] = windowOptionsFor(
  JournalWindow.Recent3m,
);

/** 维度 7「方向」—— 🔴 单选；取值与契约 `TxnDirectionFilter` 一一对应 */
export const DIRECTION_OPTIONS: readonly FilterOption<TxnDirectionFilter>[] = [
  { id: TxnDirectionFilter.Income, label: '收入' },
  { id: TxnDirectionFilter.Expense, label: '支出' },
  { id: TxnDirectionFilter.Transfer, label: '转账' },
  { id: TxnDirectionFilter.Excluded, label: '不计收支' },
];

/** 维度 8「状态」—— 最后一项 `deleted` 是**回收站开关**（契约里有专门说明） */
export const STATUS_OPTIONS: readonly FilterOption<TxnStatusFilter>[] = [
  { id: TxnStatusFilter.Uncategorized, label: '未分类' },
  { id: TxnStatusFilter.Unpaid, label: '只看未付' },
  { id: TxnStatusFilter.Refund, label: '只看退款' },
  { id: TxnStatusFilter.Deleted, label: '已删除', hint: '回收站' },
];

/** 维度 9「来源」 */
export const SOURCE_OPTIONS: readonly FilterOption<TxnSourceFilter>[] = [
  { id: TxnSourceFilter.Manual, label: '手工' },
  { id: TxnSourceFilter.Import, label: '导入批次' },
  { id: TxnSourceFilter.Schedule, label: '周期生成' },
  { id: TxnSourceFilter.Ai, label: 'AI 快记' },
];

/** 来源的中文名（行内展示用）—— 🔴 **闭合 `Record`**：新增来源取值即编译错误 */
export const SOURCE_LABELS: Record<TxnSourceFilter, string> = {
  [TxnSourceFilter.Manual]: '手工',
  [TxnSourceFilter.Import]: '导入',
  [TxnSourceFilter.Schedule]: '周期',
  [TxnSourceFilter.Ai]: 'AI 快记',
};

// ═══════════════════════ 行文案派生 ═══════════════════════

/**
 * 业务形态 → 中文（🆕 批 `153`）—— **分类名的兜底文字，不是分类本身**。
 *
 * 🔴 为什么需要它：生产数据里"未分类"的交易**全是信用卡还款与投资买卖**
 * （结构上就没有分类）。给 `—` 让人以为"分类丢了"，给「未分类」更糟 ——
 * 那句文案在说"你没填"，而事实是"这类交易本来就没有分类"。
 *
 * 🔴 **闭合 `Record`**：新增业务形态即编译错误（不会静默漏一档）。
 * ⚠️ 小程序不引本包，同表在 `miniprogram/src/journal/category-text.ts` 有一份，
 * **改文案必须两处同改**（与 `utils/money.ts` 的币种符号表同一处置）。
 */
export const BUSINESS_KIND_TEXT: Record<ParentRowBusinessKind, string> = {
  [ParentRowBusinessKind.Transfer]: '转账',
  [ParentRowBusinessKind.CreditRepayment]: '信用卡还款',
  [ParentRowBusinessKind.InvestmentBuy]: '投资买入',
  [ParentRowBusinessKind.InvestmentSell]: '投资卖出',
};

/**
 * 无分类时的文字 —— **业务形态优先，其次方向**（🆕 批 `153` 的分类列口径）。
 *
 * | 情形 | 文字 |
 * |---|---|
 * | 服务端认出业务形态 | 「信用卡还款」/「投资买入」/「投资卖出」/「转账」 |
 * | 认不出、净流入 | 「收入」 |
 * | 认不出、净流出 | 「支出」 |
 * | 认不出、净额为 0 | 「不计收支」 |
 *
 * 🔴 不再出现 `—` 与「未分类」：`—` 对用户是"系统没显示"，而「未分类」是在指控用户漏填。
 */
function categoryFallbackText(parent: TransactionRowView['parent']): string {
  const kind = parent.businessKind ?? null;
  if (kind !== null) return BUSINESS_KIND_TEXT[kind];
  if (parent.direction === 'in') return '收入';
  if (parent.direction === 'out') return '支出';
  return '不计收支';
}

/**
 * 行的主文案：**分类优先**，无分类才用业务形态 / 备注 —— `DR-066` D2 · `AC-B5.3`
 *
 * 🆕 批 `153`：插入「业务形态」一档（在此之前是"无分类 ⇒ 备注 ⇒（未分类）"）。
 * 顺序理由：业务形态回答"这笔是什么"，备注只是补充 —— 写了「还招行」的还款，
 * 主文案仍该是「信用卡还款」，而不是把备注顶上来。
 *
 * ⚠️ 这是**首页单行列表**的口径（"主文案 + 副文案"两行）。流水页自批 `143` 起改为五列，
 * 分类与备注各占一列 ⇒ 那边用 `rowCategoryText()` / `rowNoteText()`，
 * 两者共用同一个私有实现 `categoryLabel()`（防两套"等N类"规则）。
 */
export function rowTitle(row: TransactionRowView): string {
  const label = categoryLabel(row);
  if (label !== null) return label;
  if ((row.parent.businessKind ?? null) !== null) return categoryFallbackText(row.parent);
  const note = noteOf(row);
  return note !== '' ? note : categoryFallbackText(row.parent);
}

/**
 * 分类文案的**唯一实现**（`rowTitle` 与 `rowCategoryText` 共用）—— 无分类时返回 `null`。
 *
 * 🔴 抽出来是因为「前 2 个 + 等N类」这个截断规则只能有一份：两处各写一遍，
 * 改了阈值必漏一处，而**漏改不报错**（只是两处不一致）。
 */
function categoryLabel(row: TransactionRowView): string | null {
  const names = distinctCategoryNames(row);
  if (names.length === 0) return null;
  // 🔴 拆成 3 类以上时长名字会把整行挤满 ⇒ 前 2 个 + 总数（例：`餐饮、日用 等3类`）
  return names.length <= MAX_TITLE_CATEGORIES
    ? names.join('、')
    : `${names.slice(0, MAX_TITLE_CATEGORIES).join('、')} 等${String(names.length)}类`;
}

/**
 * 🆕 批 `143`（`DR-078` D2）：**「分类」列**文案 —— 🔴 批 `153` 起**不再有占位符**。
 *
 * 取值顺序：
 * 1. 有分类名 ⇒ 分类名（多分类「前 2 个 等 N 类」）；
 * 2. 无分类 ⇒ **业务形态文字**（「信用卡还款」/「投资买入」/「投资卖出」/「转账」）；
 * 3. 业务形态也认不出 ⇒ 按方向「收入」/「支出」/「不计收支」。
 *
 * 🔴 修前转账返回 `—`、其余返回 `（未分类）` —— 那是**同一件事的两套答案**，
 * 而且两套都在告诉用户"这里应该有东西但没显示"。现在给的是**它是什么**。
 *
 * 🔴 与 `rowTitle()` 的分工：`rowTitle` 是"这笔的主文案"（分类优先、无分类退备注），
 * 首页两行列表仍在用它；本函数只回答"分类这一列显示什么"。
 */
export function rowCategoryText(row: TransactionRowView): string {
  return categoryLabel(row) ?? categoryFallbackText(row.parent);
}

/**
 * 🆕 批 `153`：**腿级**分类文字 —— 展开的子行 / 抽屉的腿列表 / 账户明细用它。
 *
 * 腿没有"父行业务形态"这一层上下文（抽屉里的腿是独立渲染的），
 * 所以兜底只能按 `direction` 与 `excludeFromStats` 给文字：
 *
 * | 情形 | 文字 |
 * |---|---|
 * | 有分类名 | 分类名 |
 * | 无分类、`out` | 「支出」 |
 * | 无分类、`in` | 「收入」 |
 *
 * 🔴 **无分类时不写「不计收支」**：那是**另一层信息**，由腿行自己的标记列表达
 * （抽屉的 `full` 变体有专门一列；折叠区的 `compact` 刻意不标 —— `AC-C1.7` 的分工）。
 * 写进主文案会让同一屏出现两次「不计收支」，也破坏"两个变体只差字段多少"的既有约定。
 *
 * 🔴 修前这里写的是「未分类」：拆开的每一笔本来就各有分类，
 * 真落到这个分支的是转账两腿与还款腿 —— 它们**从来就没有分类**，
 * 说"未分类"等于让用户去找一个不存在的分类。
 */
export function legCategoryText(
  leg: Pick<TransactionLegView, 'categoryName' | 'direction'>,
): string {
  if (leg.categoryName !== null) return leg.categoryName;
  return leg.direction === 'in' ? '收入' : '支出';
}

/**
 * 🆕 批 `143`：**「备注」列**文案 —— 只取备注本身，🔴 **不拿分类兜底**。
 *
 * `rowTitle` 在无分类时用备注兜底（那是"主文案"的语义）；五列布局里分类与备注是两列，
 * 备注列空着就是空着，不能把分类名搬过去（否则同一句话出现在两列）。
 */
export function rowNoteText(row: TransactionRowView): string {
  return noteOf(row);
}

/**
 * 🆕 批 `143`：**「账户」列**文案。
 *
 * | 形态 | 输出 |
 * |---|---|
 * | 转账 | `转出账户 → 转入账户` |
 * | 其余 | 去重后的账户名，`、` 连接 |
 *
 * 🔴 转账取两端而非净额：`DR-004` 下跨币种两侧金额本就不等，净额为 0 无意义。
 */
export function rowAccountText(row: TransactionRowView): string {
  const from = row.parent.transfer;
  if (row.parent.kind === ParentRowKind.Transfer && from !== null) {
    return `${accountNameOf(row, from.fromAccountId)} → ${accountNameOf(row, from.toAccountId)}`;
  }
  return distinct(row.legs.map((l) => l.accountName)).join('、');
}

/**
 * 行的副文案 = `[备注] · 日期 · 账户 · [含 N 笔]`（缺项自动省略）。
 *
 * 🔴 **备注只在有分类时出现**（`DR-066` D2）：无分类时备注已经占了第一行，
 * 副行再写一遍就是同一句话出现两次。
 * 🔴 **分类不再进副行**：它已经在第一行了 —— 修前"分类兜底到主文案"的设计
 * 让副行成了分类的第二个出口，两处同名反而让人以为是两项。
 *
 * 🔴 转账形态**用两端账户名**（`parent.transfer`）：它的"主账户"概念不存在
 * （`DR-004` 下跨币种两侧金额本就不等，净额为 0 无意义）⇒ 显示 `转出 → 转入`。
 */
export function rowSubtitle(row: TransactionRowView): string {
  const parts: string[] = [];
  if (distinctCategoryNames(row).length > 0) {
    const note = noteOf(row);
    if (note !== '') parts.push(note);
  }
  parts.push(row.date);
  const from = row.parent.transfer;
  if (row.parent.kind === ParentRowKind.Transfer && from !== null) {
    parts.push(`${accountNameOf(row, from.fromAccountId)} → ${accountNameOf(row, from.toAccountId)}`);
  } else {
    const accounts = distinct(row.legs.map((l) => l.accountName));
    if (accounts.length > 0) parts.push(accounts.join('、'));
  }
  // 🔴 多笔（拆分 / 工资条）要让人看出"这笔不止一项" —— 文案不出现「腿」（`legs` 是数据模型词）
  if (row.legs.length > 1) parts.push(`含 ${String(row.legs.length)} 笔`);
  return parts.join(' · ');
}

/** 主文案里最多列几个分类名（`DR-066` D2：> 2 个 ⇒ `前2个 等N类`） */
const MAX_TITLE_CATEGORIES = 2;

/** 备注的**归一**形态（空白串 ≡ 没有备注 —— 修前 `'  '` 会被当成有备注） */
function noteOf(row: TransactionRowView): string {
  return (row.note ?? '').trim();
}

/** 本行的分类名（**去重保序**，跳过空名）—— 主 / 副文案共用同一份取数 */
function distinctCategoryNames(row: TransactionRowView): string[] {
  return distinct(
    row.legs
      .map((l) => l.categoryName)
      .filter((n): n is string => n !== null && n.trim() !== ''),
  );
}

/**
 * 🆕 批 `143`（`DR-078` §3 末表 · `AC-C1.55`）：**「算不算收支」的三态文案**。
 *
 * | 形态 | 判据 | 输出 |
 * |---|---|---|
 * | 整笔不计收支 | `parent.kind === Transfer` | `不计收支` |
 * | **部分**腿不计收支 | 非转账 **且** `parent.hasExcludedLegs` | `含不计收支项` |
 * | 全部计入 | 其余 | `null` |
 *
 * ## 🔴 为什么是两种文案，而不是一个词的缩写
 * - **转账** = 整笔都不进报表 ⇒「不计收支」；
 * - **工资条父项** = 实发 18,500 **计入**收入，只有社保 / 公积金那两条腿不计
 *   ⇒ 只能说「含不计收支项」，说成"不计收支"等于告诉用户"这笔工资不进报表"——**事实相反**。
 *
 * ## 🔴 判据必须「先看 kind、再看 hasExcludedLegs」
 * 修前（`excludedBadge`）只有一句 `hasExcludedLegs ? '不计收支' : null`
 * ⇒ 工资条父项被标成"不计收支"。单用该字段判是**必然**出错的，不是偶发。
 *
 * 反例守卫见 `journal-labels.spec.ts`：工资条父项**允许**「含不计收支项」、
 * **禁止**出现「不计收支」四字。
 */
export function excludedText(row: TransactionRowView): string | null {
  if (row.parent.kind === ParentRowKind.Transfer) return '不计收支';
  if (row.parent.hasExcludedLegs) return '含不计收支项';
  return null;
}

/** 记录人之外的**归属人**文字（抽屉三行用）—— 由服务端 `attribution` 算好，此处只拼 */
export function ownersText(names: readonly string[]): string {
  return names.length === 0 ? '—' : names.join('、');
}

/** 账户名：从**本行的腿**里取（数据已在响应内 ⇒ 不是第二次请求，也不是 join 口径） */
export function accountNameOf(row: TransactionRowView, accountId: string): string {
  const leg = row.legs.find((l) => l.accountId === accountId);
  return leg?.accountName ?? accountId;
}

/**
 * 腿的**展示符号投影**：`in` ⇒ 正 · `out` ⇒ 负。
 *
 * ## 🔴 这是"投影"不是"计算"，两者别混
 * `legs[].amount` 按契约恒为**正数**（`AmountInput` 明文 `.positive()`：退款用 `in` 腿表达，
 * **不许用负数 `out`**），方向由 `legs[].direction` 承担 —— 这是数据模型层的分工。
 * 而展示层需要一个**带符号的钱数**（`Amount` 的着色与 `−` 号都按符号走，`AC-C1.6`）。
 * ⇒ 本函数只做**方向 → 符号**的搬运：**不换算、不折算、不聚合**（跨币种也不参与）。
 *
 * ⚠️ 与 `parent.net` 的区别：`parent.net` 是**主账户净变动**（多腿代数和，口径在
 * `deriveParentRow`）；本函数处理的是**单条腿**（如抽屉里的逐腿明细），不要拿它替代 `parent.net`。
 */
export function legSignedCents(leg: Pick<TransactionLegView, 'direction' | 'amount'>): Cents {
  return (leg.direction === 'in' ? leg.amount : -leg.amount) as Cents;
}

/**
 * 一行的**展示金额**（含是否着色 / 是否带正负号）。
 *
 * 🔴 **只取服务端派生的 `parent`，端侧一个数都不算**：
 * - 转账 ⇒ `parent.transfer.fromAmount`（跨币种两侧本就不等，取"转出"一侧），
 *   且 **不着色**（资金搬运没有"流出/流入"的收支语义）；
 * - 其余 ⇒ `parent.net`（多笔交易的**净变动**，口径在共享层 `deriveParentRow`），着色 + 带符号。
 *
 * 🔴 **流水页与报表下钻抽屉（`AC-K2.9`）共用本函数** —— 一处实现，两处渲染
 * （各写一份是"两套口径"的开端，而这里恰好是金额）。
 */
export function rowAmount(row: TransactionRowView): {
  cents: Cents;
  colored: boolean;
  signed: boolean;
} {
  if (row.parent.kind === ParentRowKind.Transfer && row.parent.transfer !== null) {
    return { cents: row.parent.transfer.fromAmount, colored: false, signed: false };
  }
  /**
   * 🆕 批 `120`（`DR-069` D1）：**被退过的消费，行上显示净额**。
   *
   * 净额 = 本笔净额 + 已退合计（内核 `netAfterRefund()` 是唯一实现 —— 端侧不写 `+`）。
   * 🔴 不改 `parent.net`：可退额度 / 对账 / 导出都在用它，它必须保持"这一笔交易的腿净额"原义。
   * 🔴 **不再减优惠**（`DR-069` D2）：`amount` 本就是实付、`discount` 只作记录。
   */
  const net = (row.parent.net ?? 0) as Cents;
  const refunded = (row.refunds?.amount ?? 0) as Cents;
  return { cents: netAfterRefund(net, refunded), colored: true, signed: true };
}

/** 稳定去重（保序）—— 展示层的去重，不改变任何顺序口径 */
function distinct(values: readonly string[]): string[] {
  return [...new Set(values)];
}

/**
 * 🆕 批 `110`：一行的**笔级优惠合计**（整数分 · 原币）。`0` = 这笔没填过优惠。
 *
 * 🔴 只做**求和**，不派生任何东西：
 * - 「原价」= `amount + discount` 的唯一实现在共享层 `lineOriginal()`（`nucleus/discount.ts`），
 *   端侧**不得**在这里再写一次 `+`（铁律 24 防双实现）；
 * - 币种不由本函数决定 —— 调用方用 `row.parent.currency`（一行的腿同币种；
 *   跨币种转账那种多币行，优惠语义本身不存在，见 changelog §5 诚实登记）。
 */
export function rowDiscount(row: TransactionRowView): Cents {
  return sumCents(row.legs.map((l) => l.discount as Cents));
}

// ═══════════════════════ 退款可见性（批 `120` · `DR-069`） ═══════════════════════

/**
 * 这笔消费**是否已被全额退掉**（`DR-069` D4）。
 *
 * 全额退 ⇒ 金额列显示「已全额退款」而不是 `¥0.00` —— 后者会被读成"没花钱"或"数据坏了"，
 * 而全额退完其实是一笔**已结清**的消费。
 */
export function isFullyRefunded(row: TransactionRowView): boolean {
  const net = row.parent.net ?? 0;
  if (net >= 0 || row.refunds === null) return false;
  return row.refunds.amount >= -net;
}

/**
 * 被退过的消费，副行那句：`已退 ¥50.00 · 原 ¥−175.50`（`DR-069` D3）。
 *
 * 🔴 **原额必须同时可见**：用户点"退款"时要判断"当初到底花了多少"，只说"已退"不够。
 * 🔴 金额格式一律走 `formatMoneyText`（符号单源 `currency.ts` · `DR-036`），端侧不自己拼 ¥。
 */
export function refundHint(row: TransactionRowView): string | null {
  const r = row.refunds;
  if (r === null || r.amount <= 0) return null;
  const cur = row.parent.currency;
  const refunded = formatMoneyText(toCents(r.amount), cur);
  const original = formatMoneyText((row.parent.net ?? 0) as Cents, cur);
  const count = r.count > 1 ? `（${String(r.count)} 笔）` : '';
  return `已退 ${refunded}${count} · 原 ${original}`;
}

/**
 * 退款行的副行：`冲减：争鲜寿司（10-20）`（`DR-069` D5）。
 *
 * 修前退款行在列表上与普通收入行**完全同形** —— 只有点开详情抽屉才有一行"冲减自…"
 * （`TransactionDrawer.tsx`），而列表才是用户看账的地方。
 *
 * ⚠️ 父已被物理清理 ⇒ `parentBrief.date` 是空串 ⇒ 只给名字、不显示日期
 * （是否为"已删除"由 `parentDeleted` 另外标注，`AC-C2.15`）。
 */
export function chargeBackHint(row: TransactionRowView): string | null {
  const b = row.parentBrief;
  if (b === null) return null;
  const name = (b.note ?? '').trim();
  const who = name === '' ? '原交易' : name;
  const when = b.date.trim() === '' ? '' : `（${b.date.slice(5)}）`;
  return `冲减：${who}${when}`;
}

/** 第 1 级关联的**类型**（决定渲染成可点还是只读，`DR-078` §5） */
export type RelatedHintKind = 'refund' | 'installment' | 'discount' | 'chargeback';

/**
 * 一条关联的**结构化**形态 —— 🔴 不用裸字符串：`installment` 要能点击跳计划（`AC-B2.22`），
 * 而裸字符串让调用方只能靠 `includes('已分')` 反猜类型（文案一改就静默失效）。
 */
export interface RelatedHint {
  readonly kind: RelatedHintKind;
  readonly text: string;
}

/**
 * 🆕 批 `143`（`DR-078` §5）：**「关联」列**的第 1 级条目（有序）。
 *
 * 顺序**固定**（"钱变了多少"优先于"这笔挂在哪个计划上"）：
 * `已退 / 原额` > `已分 N 期` > `优惠` > `冲减自`
 *
 * ## 🔴 「含 N 笔」不在本数组里
 * 构成计数归**展开控件** `▸N`（`AC-C1.57`）。放进关联列就会和「已分 N 期」这类
 * **跨记录引用**混成一列 —— 前者说的是"这一笔内部有几项"，后者说的是"这一笔被别的记录管着"，
 * 语义不同却长得一样，用户无法区分（这是本批改版的**起点**）。
 *
 * ## 🔴 分期徽标的数据不在 `row` 里
 * 「已分 n 期」来自 `listInstallments` 的 query（`AC-B2.22`），不在 `TransactionRowView` 上
 * ⇒ 由调用方传入，本函数**不查任何东西**（纯函数，可直接断言）。
 */
export function relatedHints(
  row: TransactionRowView,
  opts: { installmentLabel?: string | null } = {},
): RelatedHint[] {
  const hints: RelatedHint[] = [];

  const refund = refundHint(row);
  if (refund !== null) hints.push({ kind: 'refund', text: refund });

  const installment = (opts.installmentLabel ?? '').trim();
  if (installment !== '') hints.push({ kind: 'installment', text: installment });

  const discount = rowDiscount(row);
  if (discount > 0) {
    hints.push({
      kind: 'discount',
      text: `优惠 ${formatMoneyText(discount, row.parent.currency)}`,
    });
  }

  const back = chargeBackHint(row);
  if (back !== null) hints.push({ kind: 'chargeback', text: back });

  return hints;
}
